import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { SessionStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs';
import { Guid } from 'guid-typescript';
import { APP_BASE_HREF } from '@angular/common';
import moment from 'moment';
import { Member } from 'src/app/member/model/member';
import { jwtDecode } from "jwt-decode";
import pkceChallenge from "pkce-challenge";

export interface Issuer
{
  id: number;
  name: string;
  uri: string;
  authorizeUri: string;
  tokenUri: string;
  clientId: string;
  exists: boolean;
  enabled: boolean;
  launch: string;
}

export interface FHIRPatientContext
{
  id: number;  // Channels Patient Id
  firstName: string;
  lastName: string;
  dob: string;
  mpi: string;
}

// Storage keys
export const FHIR_AUTH_STORAGE_KEY = 'fhirAuth';
export const FHIR_TOKEN_STORAGE_KEY = 'fhirToken';
export const FHIR_ISSUER_STORAGE_KEY = 'fhirIssuer';
export const FHIR_PATIENT_CONTEXT_STORAGE_KEY = 'fhirPatient';
export const FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY = 'fhirPatientId';
export const FHIR_CODE_VERIFIER = 'fhirCodeVerifier';
export const FHIR_FULL_SITE_STORAGE_KEY = 'fhirFullSite';
export const FHIR_NAVIGATE_TO_PATIENT_SUMMARY = 'fhirNavigateToPatientSummary';

// Default FHIR route
export const FHIR_DEFAULT_PAGE_URL = '/fhir/summary';
export const FHIR_DEFAULT_PAGE_URL_FULL_SITE = '/members/all';

@Injectable({
  providedIn: 'root'
})
export class FhirService
{
  constructor(
    private http: HttpClient,
    private sessionStorageService: SessionStorageService,
    @Inject(DOCUMENT) private document: any,
    @Inject(APP_BASE_HREF) private baseHref: string) { }

  public get fhirAuthRequest(): any
  {
    return this.sessionStorageService.retrieve(FHIR_AUTH_STORAGE_KEY);
  }

  public get oAuthToken(): any
  {
    return this.sessionStorageService.retrieve(FHIR_TOKEN_STORAGE_KEY);
  }

  public get issuer(): any
  {
    return this.sessionStorageService.retrieve(FHIR_ISSUER_STORAGE_KEY);
  }

  public get patientContext(): any
  {
    return this.sessionStorageService.retrieve(FHIR_PATIENT_CONTEXT_STORAGE_KEY);
  }

  public get patientIdContext(): number
  {
    return this.sessionStorageService.retrieve(FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY);
  }

  public get codeVerifier(): any
  {
    return this.sessionStorageService.retrieve(FHIR_CODE_VERIFIER);
  }

  public get embedded(): boolean
  {
    return this.sessionStorageService.retrieve(FHIR_PATIENT_CONTEXT_STORAGE_KEY) !== null &&
      this.patientIdContext === null;   // For EHR Simulated view, we keep the left menu but will be hidden for FHIR integration
  }

  public get fullSite(): boolean
  {
    return this.sessionStorageService.retrieve(FHIR_FULL_SITE_STORAGE_KEY);
  }

  public get navigateToPatientSummary(): boolean
  {
    return this.sessionStorageService.retrieve(FHIR_NAVIGATE_TO_PATIENT_SUMMARY);
  }

  public oAuthTokenSave(token: any): void
  {
    const tokenDob = token?.dob;
    const dob =
      tokenDob != null && String(tokenDob).trim() !== ''
        ? decodeURIComponent(String(tokenDob))
        : null;

    const patientContext: FHIRPatientContext = {
      id: null,
      firstName: token.first_name,
      lastName: token.last_name,
      dob: dob as any,
      mpi: null
    };

    // Change format from MM/DD/YYYY to YYYY/MM/DD
    if (patientContext.dob)
    {
      const [month, day, year] = patientContext.dob.split('/');
      const date = new Date(+year, +month - 1, +day);
      patientContext.dob = moment(date).format('YYYY-MM-DD').toString();
    }

    // Handle scope and store MPI
    if (token.scope && token.scope.toLowerCase().includes("fhiruser") && token.scope.toLowerCase().includes("launch/patient/")) 
    {
      const mpi = token.scope.substring(token.scope.lastIndexOf('/') + 1, token.scope.lastIndexOf(' '));
      patientContext.mpi = /\d/.test(mpi) ? mpi : null;
    }
    this.savePatientContextToStorage(patientContext);

    // Set user property from JWT token
    if (token.user == null && token.access_token != null) 
    {
      const decoded = jwtDecode(token.access_token);
      if (decoded && decoded.sub)
      {
        token.user = decoded.sub;
      }
    }
    this.sessionStorageService.store(FHIR_TOKEN_STORAGE_KEY, token);
  }

  public existsIssuer(iss: string): Observable<any>
  {
    return this.http.get(`/fhir/unauthenticated/issuer/exists?iss=${iss}`);
  }

  public existsIssuerProfile(): Observable<any>
  {
    const issuerId = this.issuer?.id ? this.issuer.id : 0;
    const profileId = this.oAuthToken?.user ? decodeURIComponent(this.oAuthToken.user) : null;
    return this.http.get(`/fhir/unauthenticated/issuer/${issuerId}/profile/exists?profileId=${profileId}`);
  }

  public async getAuthEndpoint(issuer: Issuer, launchCode: string, fullSite: boolean): Promise<string>
  {
    const data = await pkceChallenge();
    const codeVerifier = data.code_verifier;
    const codeChallenge = data.code_challenge;

    const params = {
      scope: encodeURIComponent('launch openid fhirUser'),
      response_type: 'code',
      redirect_uri: `${this.document.location.origin}${this.baseHref}fhir/redirect`,
      launch: launchCode,
      client_id: issuer.clientId,
      state: Guid.create().toString(),
      aud: encodeURIComponent(issuer.uri),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    };

    // Build query params
    const paramsArray = [];
    for (const key of Object.keys(params))
    {
      paramsArray.push(`${key}=${params[key]}`);
    }

    this.sessionStorageService.store(FHIR_AUTH_STORAGE_KEY, params);
    this.sessionStorageService.store(FHIR_ISSUER_STORAGE_KEY, issuer);
    this.sessionStorageService.store(FHIR_CODE_VERIFIER, codeVerifier);
    this.savePatientContextFullSiteToStorage(fullSite);

    return `${issuer.authorizeUri}?${paramsArray.join('&')}`;
  }

  public callTokenEndpoint(authCode: string): Observable<any>
  {
    const fhirAuthRequest = this.sessionStorageService.retrieve(FHIR_AUTH_STORAGE_KEY);
    const authRequest: any = {};
    authRequest.issuerId = this.issuer.id;
    authRequest.code = authCode;
    authRequest.redirectUri = fhirAuthRequest.redirect_uri;
    authRequest.clientId = fhirAuthRequest.client_id;
    authRequest.codeVerifier = this.sessionStorageService.retrieve(FHIR_CODE_VERIFIER);

    return this.http.post('/fhir/unauthenticated/auth/token', authRequest);
  }

  public clearStorage(clearPatientContext = false): void
  {
    this.sessionStorageService.clear(FHIR_AUTH_STORAGE_KEY);
    this.sessionStorageService.clear(FHIR_TOKEN_STORAGE_KEY);
    this.sessionStorageService.clear(FHIR_ISSUER_STORAGE_KEY);
    this.sessionStorageService.clear(FHIR_CODE_VERIFIER);
    if (clearPatientContext)
    {
      this.sessionStorageService.clear(FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY);
      this.sessionStorageService.clear(FHIR_PATIENT_CONTEXT_STORAGE_KEY);
      this.sessionStorageService.clear(FHIR_FULL_SITE_STORAGE_KEY);
    }
  }

  public saveChannelsPatient(member: Member): void
  {
    const patientContext = this.patientContext || {};
    patientContext.id = member ? member.id : null;
    patientContext.firstName = member ? (member.givenName ?? member.name?.givenName) : null;
    patientContext.lastName = member ? (member.familyName ?? member.name?.familyName) : null;
    patientContext.dob = member ? member.dateOfBirth : null;
    this.savePatientContextToStorage(patientContext);
  }

  public saveChannelsPatientId(id: number): void
  {
    this.sessionStorageService.store(FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY, id);
  }

  public savePatientContextToStorage(patientContext: any): void 
  {
    this.sessionStorageService.store(FHIR_PATIENT_CONTEXT_STORAGE_KEY, patientContext);
  }

  public savePatientContextFullSiteToStorage(fullSite: boolean): void
  {
    this.sessionStorageService.store(FHIR_FULL_SITE_STORAGE_KEY, fullSite);
  }
}
